<!-- src/views/Restaurante/ModificadorDetalle.vue -->
<template>
  <div class="p-3 sm:p-6 text-lexend overflow-x-hidden min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-900/80">

    <!-- HEADER -->
    <div class="mb-6 animate-fadeIn">
      <button @click="$router.push({ name: 'ModificadoresList' })"
        class="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-black text-gray-600 dark:text-gray-300 hover:bg-gray-50 transition-all">
        <Icon icon="mdi:arrow-left" class="text-base" /> Volver a Modificadores
      </button>
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div class="flex items-center gap-2 mb-1">
            <div class="w-10 h-1.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
            <span class="text-[10px] font-black text-orange-600 dark:text-orange-400 tracking-widest uppercase">Detalle Modificador</span>
          </div>
          <h1 class="text-2xl sm:text-4xl font-black text-gray-900 dark:text-white flex items-center flex-wrap gap-3">
            {{ modificador?.nombre || 'Cargando...' }}
            <span v-if="modificador?.es_obligatorio"
              class="px-2 py-1 bg-red-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest">
              Obligatorio
            </span>
          </h1>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium">
            Tipo: {{ modificador?.tipo || '—' }} · {{ modificador?.seleccion_multiple ? `Múltiple (máx ${modificador.max_selecciones})` : 'Selección única' }}
          </p>
        </div>

        <!-- Badge tasa -->
        <div v-if="tasaVentaActual > 0"
          class="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded-xl shadow-lg">
          <Icon icon="mdi:currency-exchange" class="text-lg" />
          <div>
            <p class="text-[9px] font-black uppercase tracking-widest opacity-90">Tasa Venta</p>
            <p class="text-sm font-black">Bs {{ formatearMonto(tasaVentaActual, 2) }}</p>
          </div>
        </div>
        <div v-else class="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-800 rounded-xl">
          <Icon icon="mdi:alert-circle-outline" class="text-gray-500 text-lg" />
          <p class="text-xs font-bold text-gray-500">Sin tasa configurada</p>
        </div>
      </div>
    </div>

    <!-- STATS -->
    <div class="grid grid-cols-2 gap-3 mb-6">
      <div class="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
        <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Opciones</p>
        <p class="text-3xl font-black text-orange-600">{{ opciones.length }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
        <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Productos Asignados</p>
        <p class="text-3xl font-black text-blue-600">{{ productosAsignados.length }}</p>
      </div>
    </div>

    <!-- 2 COLUMNAS -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

      <!-- OPCIONES -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden animate-fadeInUp">
        <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Icon icon="mdi:format-list-bulleted" class="text-orange-600 text-xl" />
            <h3 class="text-sm font-black uppercase tracking-widest">Opciones</h3>
          </div>
          <button @click="openCreateOpcion"
            class="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-black text-[10px] uppercase flex items-center gap-1">
            <Icon icon="mdi:plus" class="text-sm" /> Agregar
          </button>
        </div>
        <div class="p-3 max-h-[65vh] overflow-y-auto custom-scrollbar space-y-2">
          <div v-if="loadingOpciones" class="text-center py-10">
            <Icon icon="mdi:loading" class="animate-spin text-3xl text-gray-400" />
          </div>
          <div v-else-if="opciones.length === 0" class="text-center py-10 opacity-50">
            <Icon icon="mdi:format-list-bulleted-type" class="text-5xl mb-2 text-gray-300 mx-auto" />
            <p class="text-xs font-bold text-gray-400">Sin opciones</p>
          </div>
          <div v-else v-for="op in opciones" :key="op.idOpcionModificador"
            class="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 border border-gray-100 dark:border-gray-700 flex items-start gap-2">
            <div class="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center shrink-0">
              <Icon icon="mdi:circle-medium" class="text-orange-600" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <p class="text-sm font-black text-gray-900 dark:text-white">{{ op.nombre }}</p>
                <Icon v-if="op.es_default" icon="mdi:star" class="text-amber-400 text-xs" />
                <span v-if="op.estado === 'inactivo'"
                  class="px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded text-[9px] font-black">INACTIVO</span>
              </div>
              <div class="flex gap-3 mt-1 text-[10px] font-bold">
                <span v-if="op.precio_extra_usd > 0" class="text-emerald-600">+ ${{ formatearMonto(op.precio_extra_usd, 2) }}</span>
                <span v-if="op.precio_extra_bs > 0" class="text-blue-600">+ Bs {{ formatearMonto(op.precio_extra_bs, 2) }}</span>
                <span v-if="op.precio_extra_usd === 0 && op.precio_extra_bs === 0" class="text-gray-400">Sin costo extra</span>
              </div>
            </div>
            <div class="flex gap-1 shrink-0">
              <button @click="openEditOpcion(op)"
                class="p-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-amber-500 rounded-lg hover:bg-amber-50">
                <Icon icon="mdi:pencil" class="text-sm" />
              </button>
              <button @click="abrirModalEliminarOpcion(op)"
                class="p-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-rose-500 rounded-lg hover:bg-rose-50">
                <Icon icon="mdi:delete" class="text-sm" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- PRODUCTOS ASIGNADOS -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-100">
        <div class="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Icon icon="mdi:package-variant" class="text-blue-600 text-xl" />
            <h3 class="text-sm font-black uppercase tracking-widest">Productos</h3>
          </div>
          <button @click="openAsignarProducto"
            class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-black text-[10px] uppercase flex items-center gap-1">
            <Icon icon="mdi:plus" class="text-sm" /> Asignar
          </button>
        </div>
        <div class="p-3 max-h-[65vh] overflow-y-auto custom-scrollbar space-y-2">
          <div v-if="loadingProductos" class="text-center py-10">
            <Icon icon="mdi:loading" class="animate-spin text-3xl text-gray-400" />
          </div>
          <div v-else-if="productosAsignados.length === 0" class="text-center py-10 opacity-50">
            <Icon icon="mdi:package-variant-remove" class="text-5xl mb-2 text-gray-300 mx-auto" />
            <p class="text-xs font-bold text-gray-400">Sin productos asignados</p>
          </div>
          <div v-else v-for="pa in productosAsignados" :key="pa.idModificadorProducto"
            class="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 border border-gray-100 dark:border-gray-700 flex items-center gap-2">
            <div class="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
              <Icon icon="mdi:food" class="text-blue-600" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-black text-gray-900 dark:text-white truncate">
                {{ pa.nombreMenuProducto || `Producto #${pa.idMenuProducto}` }}
              </p>
              <p class="text-[10px] font-bold text-gray-400">ID menú: {{ pa.idMenuProducto }}</p>
            </div>
            <button @click="abrirModalQuitarProducto(pa)"
              class="p-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-rose-500 rounded-lg hover:bg-rose-50">
              <Icon icon="mdi:link-off" class="text-sm" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL OPCION -->
    <Teleport to="body">
      <div v-if="showOpcionModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="closeOpcionModal">
        <div class="bg-white dark:bg-gray-800 w-full max-w-lg sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn flex flex-col max-h-[90vh]">
          <div class="bg-gradient-to-r from-orange-600 to-red-600 px-5 py-4 flex items-center gap-3 shrink-0">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editingOpcion ? 'mdi:pencil-box' : 'mdi:format-list-bulleted'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-base font-black text-white">{{ editingOpcion ? 'Editar Opción' : 'Nueva Opción' }}</h3>
            </div>
            <button @click="closeOpcionModal" class="ml-auto p-2 bg-white/10 rounded-xl">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>
          <div class="p-5 overflow-y-auto custom-scrollbar">
            <form @submit.prevent="saveOpcion" class="space-y-4">

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Nombre *</label>
                <input v-model="formOp.nombre" required maxlength="100"
                  class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-orange-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none"
                  placeholder="Ej: Término medio, Sin cebolla" />
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div class="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-2xl p-4 border-2 border-emerald-200 dark:border-emerald-800/50">
                  <label class="block text-[10px] font-black text-emerald-700 dark:text-emerald-300 uppercase mb-2 tracking-widest">Extra USD</label>
                  <div class="relative">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-black">$</span>
                    <input
                      :value="formUSDDisplay"
                      @input="onInputPrecioUSD"
                      type="text"
                      inputmode="decimal"
                      placeholder="0,00"
                      class="w-full pl-8 pr-3 py-3 bg-white dark:bg-gray-800 border-2 border-emerald-200 dark:border-emerald-800/50 focus:border-emerald-500 rounded-xl text-base font-black text-emerald-700 dark:text-emerald-300 outline-none text-right tabular-nums" />
                  </div>
                  <p class="text-[9px] text-emerald-600 mt-1 font-bold">Formato: 33.655,00</p>
                </div>
                <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-4 border-2 border-blue-200 dark:border-blue-800/50">
                  <label class="block text-[10px] font-black text-blue-700 dark:text-blue-300 uppercase mb-2 tracking-widest">Extra Bs</label>
                  <div class="relative">
                    <span class="absolute left-3 top-1/2 -translate-y-1/2 text-blue-600 font-black text-xs">Bs</span>
                    <input
                      :value="formBsDisplay"
                      @input="onInputPrecioBs"
                      type="text"
                      inputmode="decimal"
                      placeholder="0,00"
                      class="w-full pl-9 pr-3 py-3 bg-white dark:bg-gray-800 border-2 border-blue-200 dark:border-blue-800/50 focus:border-blue-500 rounded-xl text-base font-black text-blue-700 dark:text-blue-300 outline-none text-right tabular-nums" />
                  </div>
                  <p class="text-[9px] text-blue-600 mt-1 font-bold">Formato: 336.550,00</p>
                </div>
              </div>

              <div v-if="tasaVentaActual > 0"
                class="flex items-center justify-center gap-1.5 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl">
                <Icon icon="mdi:currency-exchange" class="text-amber-600 text-sm" />
                <span class="text-[10px] font-black text-amber-700 dark:text-amber-300 uppercase tracking-widest">
                  Se calcula con la tasa de venta: Bs {{ formatearMonto(tasaVentaActual, 2) }}
                </span>
              </div>
              <div v-else
                class="flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl">
                <Icon icon="mdi:alert-circle-outline" class="text-gray-400 text-sm" />
                <span class="text-[10px] font-bold text-gray-500">Sin tasa configurada — calcula Bs manualmente</span>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Orden</label>
                  <input v-model.number="formOp.orden" type="number" min="0"
                    class="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-orange-500 rounded-xl text-sm font-bold text-center" />
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Default</label>
                  <label class="flex items-center gap-2 mt-2 cursor-pointer">
                    <input type="checkbox" v-model="formOp.es_default" class="w-5 h-5 rounded text-amber-500" />
                    <span class="text-xs font-black text-gray-700 dark:text-gray-300">⭐ Por defecto</span>
                  </label>
                </div>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 uppercase mb-3 tracking-widest">Estado</label>
                <div class="grid grid-cols-2 gap-3">
                  <label :class="['flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-black text-xs cursor-pointer',
                    formOp.estado === 'activo' ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-white border-gray-200 text-gray-500']">
                    <input type="radio" v-model="formOp.estado" value="activo" class="hidden" /> Activo
                  </label>
                  <label :class="['flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-black text-xs cursor-pointer',
                    formOp.estado === 'inactivo' ? 'bg-rose-50 border-rose-500 text-rose-700' : 'bg-white border-gray-200 text-gray-500']">
                    <input type="radio" v-model="formOp.estado" value="inactivo" class="hidden" /> Inactivo
                  </label>
                </div>
              </div>

              <div class="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button type="button" @click="closeOpcionModal"
                  class="w-full sm:w-auto px-6 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 rounded-xl font-black text-xs uppercase tracking-widest">
                  Cancelar
                </button>
                <button type="submit" :disabled="savingOpcion"
                  class="w-full flex-1 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl font-black text-xs uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg">
                  <Icon :icon="savingOpcion ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': savingOpcion}" class="text-lg" />
                  {{ savingOpcion ? 'Guardando...' : (editingOpcion ? 'Actualizar' : 'Crear') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL ASIGNAR PRODUCTO -->
    <Teleport to="body">
      <div v-if="showAsignarModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="closeAsignarModal">
        <div class="bg-white dark:bg-gray-800 w-full max-w-lg sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn flex flex-col max-h-[90vh]">
          <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4 flex items-center gap-3 shrink-0">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon icon="mdi:link-plus" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-base font-black text-white">Asignar a Producto del Menú</h3>
              <p class="text-[11px] text-white/80 font-bold">Busca un producto de menú</p>
            </div>
            <button @click="closeAsignarModal" class="ml-auto p-2 bg-white/10 rounded-xl">
              <Icon icon="mdi:close" class="text-white text-xl" />
            </button>
          </div>
          <div class="p-5 overflow-y-auto custom-scrollbar">
            <div class="bg-gray-50 dark:bg-gray-900/30 rounded-2xl p-4 border border-gray-100 dark:border-gray-700">
              <div class="relative">
                <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                <input v-model="busquedaProducto" type="text" placeholder="Buscar producto del menú..."
                  class="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-blue-500 rounded-xl text-sm font-bold outline-none" />
              </div>
              <div class="mt-3 max-h-72 overflow-y-auto custom-scrollbar space-y-2">
                <div v-if="cargandoMenuProductos" class="text-center py-6">
                  <Icon icon="mdi:loading" class="animate-spin text-2xl text-blue-500" />
                </div>
                <div v-else-if="menuProductosFiltrados.length === 0" class="text-center py-6 opacity-50">
                  <Icon icon="mdi:package-variant-remove" class="text-4xl mb-2 text-gray-300 mx-auto" />
                  <p class="text-xs font-bold text-gray-400">
                    {{ menuProductos.length === 0 ? 'No hay productos' : 'Todos ya están asignados o no coinciden' }}
                  </p>
                </div>
                <button v-else v-for="mp in menuProductosFiltrados" :key="mp.idMenuProducto"
                  type="button" @click="asignarProducto(mp)"
                  class="w-full text-left p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl hover:border-blue-400 hover:shadow transition-all flex items-center gap-2">
                  <Icon icon="mdi:food" class="text-blue-600 text-lg" />
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-black text-gray-900 dark:text-white truncate">
                      {{ mp.nombreProducto || `Producto #${mp.idProducto}` }}
                    </p>
                    <p class="text-[10px] font-bold text-gray-400">{{ mp.nombreCategoria }} · ID menú: {{ mp.idMenuProducto }}</p>
                  </div>
                  <Icon icon="mdi:plus-circle" class="text-blue-600 text-xl" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL CONFIRMACIÓN -->
    <Teleport to="body">
      <div v-if="showConfirmModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10002] flex items-end sm:items-center justify-center sm:p-4"
        @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 w-full max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-slideUp sm:animate-zoomIn">
          <div class="px-5 py-4" :class="confirmModalColor">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon :icon="confirmModalIcon" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-base font-black text-white">{{ confirmModalTitulo }}</h3>
                <p class="text-[11px] text-white/80 font-bold">{{ confirmModalSubtitulo }}</p>
              </div>
            </div>
          </div>
          <div class="p-6 text-center">
            <p class="text-sm font-bold text-gray-700 dark:text-gray-300">{{ confirmModalMensaje }}</p>
            <p v-if="confirmModalDetalle" class="text-xs text-gray-500 mt-2">{{ confirmModalDetalle }}</p>
          </div>
          <div class="px-5 py-4 bg-gray-50 dark:bg-gray-900 border-t flex flex-col-reverse sm:flex-row gap-3">
            <button @click="cancelarConfirmacion"
              class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 rounded-xl font-black text-xs uppercase">
              Cancelar
            </button>
            <button @click="ejecutarAccionConfirmada" :disabled="confirmModalCargando"
              class="w-full sm:flex-[2] py-3 rounded-xl font-black text-xs uppercase text-white flex items-center justify-center gap-2 shadow-lg"
              :class="confirmModalButtonColor">
              <Icon :icon="confirmModalCargando ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': confirmModalCargando}" class="text-lg" />
              {{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import Swal from 'sweetalert2';
import {
  obtenerModificadorPorId, obtenerOpcionesPorModificador, crearOpcion, actualizarOpcion, eliminarOpcion,
  obtenerProductosPorModificador, asignarModificadorAProducto, quitarModificadorDeProducto,
  formatearMonto,
} from '@/api/modificadores.js';
import { obtenerProductos } from '@/api/menus.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';

export default {
  name: 'ModificadorDetalle',
  components: { Icon },
  data() {
    return {
      modificador: null,
      opciones: [],
      productosAsignados: [],
      menuProductos: [],
      loadingOpciones: false,
      loadingProductos: false,
      cargandoMenuProductos: false,
      busquedaProducto: '',

      tasaVentaActual: 0,
      ultimoCampoEditado: 'usd',

      showOpcionModal: false, savingOpcion: false, editingOpcion: null,
      formOp: {
        nombre: '', precio_extra_usd: 0, precio_extra_bs: 0,
        es_default: false, orden: 0, estado: 'activo',
      },
      formUSDDisplay: '',
      formBsDisplay: '',

      showAsignarModal: false,

      showConfirmModal: false, confirmModalTitulo: '', confirmModalSubtitulo: '',
      confirmModalMensaje: '', confirmModalDetalle: '', confirmModalIcon: '',
      confirmModalColor: '', confirmModalButtonColor: '', confirmModalBotonTexto: '',
      confirmModalCargando: false, confirmModalAccion: null, confirmModalDatos: null,
    };
  },
  computed: {
    // ✅ Filtra ya asignados → evita el 409
    menuProductosFiltrados() {
      const yaAsignados = new Set(this.productosAsignados.map(p => p.idMenuProducto));
      let lista = this.menuProductos.filter(mp => !yaAsignados.has(mp.idMenuProducto));

      if (!this.busquedaProducto.trim()) return lista.slice(0, 20);
      const t = this.busquedaProducto.toLowerCase();
      return lista
        .filter(mp =>
          mp.nombreProducto?.toLowerCase().includes(t) ||
          mp.nombreCategoria?.toLowerCase().includes(t) ||
          String(mp.idMenuProducto).includes(t)
        )
        .slice(0, 20);
    },
  },
  async mounted() {
    await this.cargarTasaVenta();
    await this.cargarModificador();
    await this.cargarOpciones();
    await this.cargarProductosAsignados();
  },
  methods: {
    mostrarToast(icon, title, text = '') {
      Swal.fire({
        toast: true, position: 'top-end', icon, title, text,
        showConfirmButton: false, timer: 3000, timerProgressBar: true,
      });
    },

    // ===== FORMATEO =====
    formatearInputMonto(valor) {
      if (valor === '' || valor === null || valor === undefined) return '';
      let str = String(valor).replace(/[^\d,]/g, '');
      if (!str.includes(',')) str = str + ',';
      let [entero, decimales = ''] = str.split(',');
      entero = entero.replace(/^0+(?=\d)/, '');
      const enteroF = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      decimales = decimales.substring(0, 2);
      return decimales.length > 0 || str.includes(',')
        ? `${enteroF},${decimales}`
        : enteroF;
    },
    parsearMontoInput(str) {
      if (!str && str !== 0) return null;
      const limpio = String(str).replace(/\./g, '').replace(',', '.');
      const n = parseFloat(limpio);
      return isNaN(n) ? null : n;
    },

    // ===== AUTO-CÁLCULO USD ↔ Bs =====
    onInputPrecioUSD(e) {
      this.ultimoCampoEditado = 'usd';
      this.formUSDDisplay = this.formatearInputMonto(e.target.value);
      this.formOp.precio_extra_usd = this.parsearMontoInput(this.formUSDDisplay) || 0;

      if (this.tasaVentaActual > 0) {
        const bs = this.formOp.precio_extra_usd * this.tasaVentaActual;
        this.formOp.precio_extra_bs = Number(bs.toFixed(2));
        this.formBsDisplay = formatearMonto(this.formOp.precio_extra_bs, 2);
      }
    },
    onInputPrecioBs(e) {
      this.ultimoCampoEditado = 'bs';
      this.formBsDisplay = this.formatearInputMonto(e.target.value);
      this.formOp.precio_extra_bs = this.parsearMontoInput(this.formBsDisplay) || 0;

      if (this.tasaVentaActual > 0) {
        const usd = this.formOp.precio_extra_bs / this.tasaVentaActual;
        this.formOp.precio_extra_usd = Number(usd.toFixed(2));
        this.formUSDDisplay = formatearMonto(this.formOp.precio_extra_usd, 2);
      }
    },

    async cargarTasaVenta() {
      try {
        const r = await obtenerTasaVentaActual();
        if (r.success && r.data) {
          this.tasaVentaActual = Number(
            r.data.precio_usd_bs || r.data.precioUsdBs || r.data.PrecioUsdBs || 0
          );
        }
      } catch (e) {
        console.warn('No se pudo obtener la tasa de venta:', e);
      }
    },

    async cargarModificador() {
      const id = Number(this.$route.params.id);
      if (!id) return;
      const r = await obtenerModificadorPorId(id);
      if (r.success) this.modificador = r.data;
    },
    async cargarOpciones() {
      this.loadingOpciones = true;
      try {
        const id = Number(this.$route.params.id);
        const r = await obtenerOpcionesPorModificador(id);
        if (r.success) this.opciones = (r.data || []).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      } finally { this.loadingOpciones = false; }
    },
    async cargarProductosAsignados() {
      this.loadingProductos = true;
      try {
        const id = Number(this.$route.params.id);
        const r = await obtenerProductosPorModificador(id);
        if (r.success) this.productosAsignados = r.data || [];
      } finally { this.loadingProductos = false; }
    },
    async cargarMenuProductos() {
      this.cargandoMenuProductos = true;
      try {
        const r = await obtenerProductos();
        if (r.success) this.menuProductos = r.data || [];
      } finally { this.cargandoMenuProductos = false; }
    },

    // ===== OPCIONES CRUD =====
    openCreateOpcion() {
      this.editingOpcion = null;
      const maxOrden = this.opciones.reduce((m, o) => Math.max(m, o.orden ?? 0), 0);
      this.formOp = {
        nombre: '', precio_extra_usd: 0, precio_extra_bs: 0,
        es_default: false, orden: maxOrden + 1, estado: 'activo',
      };
      this.formUSDDisplay = '';
      this.formBsDisplay = '';
      this.ultimoCampoEditado = 'usd';
      this.showOpcionModal = true;
    },
    openEditOpcion(op) {
      this.editingOpcion = op;
      this.formOp = {
        nombre: op.nombre,
        precio_extra_usd: op.precio_extra_usd || 0,
        precio_extra_bs: op.precio_extra_bs || 0,
        es_default: !!op.es_default,
        orden: op.orden ?? 0,
        estado: op.estado,
      };
      this.formUSDDisplay = op.precio_extra_usd ? formatearMonto(op.precio_extra_usd, 2) : '';
      this.formBsDisplay = op.precio_extra_bs ? formatearMonto(op.precio_extra_bs, 2) : '';
      this.ultimoCampoEditado = 'usd';
      this.showOpcionModal = true;
    },
    closeOpcionModal() { this.showOpcionModal = false; },
    async saveOpcion() {
      this.savingOpcion = true;
      try {
        const payload = {
          ...this.formOp,
          idModificador: Number(this.$route.params.id),
          precio_extra_usd: this.parsearMontoInput(this.formUSDDisplay) || 0,
          precio_extra_bs: this.parsearMontoInput(this.formBsDisplay) || 0,
        };
        const r = this.editingOpcion
          ? await actualizarOpcion(this.editingOpcion.idOpcionModificador, payload)
          : await crearOpcion(payload);
        if (r.success) {
          await this.cargarOpciones();
          this.closeOpcionModal();
          this.mostrarToast('success', '¡Éxito!', `Opción ${this.editingOpcion ? 'actualizada' : 'creada'}.`);
        } else {
          this.mostrarToast('error', 'Error', r.error || 'Error al guardar');
        }
      } finally { this.savingOpcion = false; }
    },
    abrirModalEliminarOpcion(op) {
      this.abrirModalConfirmacion({
        titulo: 'Desactivar Opción', subtitulo: 'Cambiará a inactivo',
        mensaje: `¿Desactivar "${op.nombre}"?`,
        detalle: 'La opción se marcará como inactiva.',
        icon: 'mdi:alert-circle',
        color: 'bg-gradient-to-r from-red-600 to-pink-600',
        buttonColor: 'bg-gradient-to-r from-red-600 to-pink-600',
        botonTexto: 'Desactivar', datos: op.idOpcionModificador,
        accion: (id) => this.deleteOpcion(id),
      });
    },
    async deleteOpcion(id) {
      const r = await eliminarOpcion(id);
      if (r.success) {
        await this.cargarOpciones();
        this.mostrarToast('success', 'Desactivada', 'Opción desactivada.');
      } else {
        this.mostrarToast('error', 'Error', r.error || 'Error');
      }
    },

    // ===== ASIGNAR PRODUCTO =====
    async openAsignarProducto() {
      this.busquedaProducto = '';
      await this.cargarMenuProductos();
      await this.cargarProductosAsignados();  // refrescar para filtrar bien
      this.showAsignarModal = true;
    },
    closeAsignarModal() { this.showAsignarModal = false; },
    async asignarProducto(mp) {
      const idMod = Number(this.$route.params.id);
      const r = await asignarModificadorAProducto(mp.idMenuProducto, idMod);
      if (r.success) {
        this.closeAsignarModal();
        await this.cargarProductosAsignados();
        this.mostrarToast('success', 'Asignado', 'Producto asignado al modificador.');
      } else {
        // ✅ Manejo amigable del 409 (ya asignado)
        const msg = (r.error || '').toLowerCase();
        if (msg.includes('ya está asignado') || msg.includes('ya esta asignado') || msg.includes('conflict') || msg.includes('409')) {
          this.mostrarToast('warning', 'Ya asignado', 'Ese producto ya tiene este modificador.');
          await this.cargarProductosAsignados();
          this.closeAsignarModal();
        } else {
          this.mostrarToast('error', 'Error', r.error || 'No se pudo asignar');
        }
      }
    },
    abrirModalQuitarProducto(pa) {
      this.abrirModalConfirmacion({
        titulo: 'Quitar Producto', subtitulo: 'Se desvincula',
        mensaje: `¿Quitar "${pa.nombreMenuProducto || 'producto'}" del modificador?`,
        detalle: 'El producto del menú seguirá existiendo.',
        icon: 'mdi:link-off',
        color: 'bg-gradient-to-r from-orange-500 to-amber-600',
        buttonColor: 'bg-gradient-to-r from-orange-500 to-amber-600',
        botonTexto: 'Quitar', datos: pa.idModificadorProducto,
        accion: (id) => this.quitarProducto(id),
      });
    },
    async quitarProducto(id) {
      const r = await quitarModificadorDeProducto(id);
      if (r.success) {
        await this.cargarProductosAsignados();
        this.mostrarToast('success', 'Quitado', 'Producto quitado del modificador.');
      } else {
        this.mostrarToast('error', 'Error', r.error || 'Error');
      }
    },

    // ===== CONFIRM =====
    abrirModalConfirmacion(config) {
      this.confirmModalTitulo = config.titulo || 'Confirmar';
      this.confirmModalSubtitulo = config.subtitulo || '';
      this.confirmModalMensaje = config.mensaje || '¿Estás seguro?';
      this.confirmModalDetalle = config.detalle || '';
      this.confirmModalIcon = config.icon || 'mdi:help-circle';
      this.confirmModalColor = config.color || 'bg-gradient-to-r from-orange-600 to-red-600';
      this.confirmModalButtonColor = config.buttonColor || 'bg-gradient-to-r from-orange-600 to-red-600';
      this.confirmModalBotonTexto = config.botonTexto || 'Confirmar';
      this.confirmModalAccion = config.accion;
      this.confirmModalDatos = config.datos || null;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalCargando = false;
      this.confirmModalAccion = null;
    },
    async ejecutarAccionConfirmada() {
      if (this.confirmModalAccion) {
        this.confirmModalCargando = true;
        await this.confirmModalAccion(this.confirmModalDatos);
        this.confirmModalCargando = false;
        this.cancelarConfirmacion();
      }
    },

    formatearMonto,
  },
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #475569; }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
.animate-zoomIn { animation: zoomIn 0.25s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
.animate-slideUp { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
.delay-100 { animation-delay: 0.1s; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@media (max-width: 768px) {
  button { min-height: 44px; }
  input, select, textarea { font-size: 16px !important; }
}
</style>